import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { selectGymData } from "../../../redux/reducers/gymDataSlice";
import { fetchGymData } from "../../../data/APIs";
import CommonHeadingNBtns from "../../common-components/CommonHeadingNBtns";
import Loader from "../../ui/Loader";
import SelectDropdown from "../../components-react/SelectDropdown";
import "../../../styles/centralised-plan/cpmap.css";
import { fetchCentralisedPlansData } from "../../../redux/reducers/dropdownApiSlice";
import CentralisedForm from "./CentralisedForm";
import { commonPostPutApi } from "../../../redux/reducers/commonApiSlice";
import { showConfirmAlert } from "../../components-react/ReactAlert";

const CPMapForm = ({ closeModal, rowData, userInfoData, callFunction }) => {
  const dispatch = useDispatch();
  const gymData = useSelector(selectGymData);
  const { centralisedPlans } = useSelector((state) => state.dropdown);

  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedGyms, setSelectedGyms] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [step, setStep] = useState(1);
  const [selectedPlans, setSelectedPlans] = useState({});
  const [allPlansOptions, setAllPlansOptions] = useState([]);

  const fetchGyms = async (searchStr = "") => {
    setIsLoading(true);

    try {
      const query = `&category_id=${rowData?.category_id || ""}`;

      await dispatch(
        fetchGymData(
          userInfoData?.token,
          page,
          limit,
          searchStr,
          "active",
          query
        )
      );
    } catch (error) {
      console.error("Error fetching gyms:", error);
      toast.error("Error loading gyms");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllApplicablePlans = async (filters) => {
    dispatch(
      fetchCentralisedPlansData({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  };

  const handleSearch = () => {
    setPage(1);
    fetchGyms(searchQuery);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleGymSelect = (gymId) => {
    setSelectedGyms((prev) => {
      if (prev.includes(gymId)) {
        const newSelectedPlans = { ...selectedPlans };
        delete newSelectedPlans[gymId];
        setSelectedPlans(newSelectedPlans);
        return prev.filter((id) => id !== gymId);
      }
      return [...prev, gymId];
    });
  };

  const handleSelectAll = () => {
    if (selectedGyms.length === gymData.length) {
      setSelectedGyms([]);
      setSelectedPlans({});
    } else {
      setSelectedGyms(gymData.map((gym) => gym.value));
    }
  };

  const handleNext = () => {
    if (step === 1) {
      if (selectedGyms.length === 0) {
        toast.error("Please select at least one gym");
        return;
      }
      setStep(2);
    } else if (step === 2) {
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    } else if (step === 3) {
      setStep(2);
    }
  };

  const handleSubmit = async () => {
    const handleConfirm = async () => {
      try {
        const gymsData = selectedGyms.map((gymId) => {
          return {
            gym_id: gymId,
            mapped_id: selectedPlans[gymId]?.value || "",
          };
        });

        const payload = {
          gyms_data: gymsData,
          centralized_id: rowData?.uid,
        };

        const method = "POST";
        const endpoint = "/plan/draft/V2/map";
        const token = userInfoData?.token || "";

        const result = await dispatch(
          commonPostPutApi({ method, endpoint, payload, token })
        );

        if (result?.payload?.status) {
          toast.success(
            result?.payload?.message || "Plans mapped successfully!"
          );
          closeModal();
          if (typeof callFunction === "function") callFunction();
        } else {
          const errorMessage =
            result?.payload?.message || result?.error?.message;
          toast.error(errorMessage || "Error mapping plans");
        }
      } catch (error) {
        console.error("Error making POST request:", error);
        toast.error("Error mapping plans");
      }
    };

    showConfirmAlert(
      handleConfirm,
      "Are you sure you want to map these plans?",
      "Map Plans",
      "Confirm"
    );
  };

  useEffect(() => {
    fetchGyms();
  }, [page, rowData?.category_id]);

  useEffect(() => {
    if (step === 2 && selectedGyms.length > 0) {
      const sendableFilters = {
        page: 1,
        limit: 1000,
        status: "active",
        is_goal: 0,
        duration: rowData?.duration || 180,
      };
      fetchAllApplicablePlans(sendableFilters);
    } else if (step !== 2) {
      setAllPlansOptions([]);
    }
  }, [step, selectedGyms]);

  useEffect(() => {
    if (searchQuery === "") {
      fetchGyms("");
    }
  }, [searchQuery]);

  useEffect(() => {
    if (centralisedPlans?.dropdown?.length > 0) {
      setAllPlansOptions(centralisedPlans.dropdown);
    } else {
      setAllPlansOptions([]);
    }
  }, [centralisedPlans]);

  return (
    <div className="cpmap-container">
      <CommonHeadingNBtns
        title={step === 3 ? "Map Plan with Gyms" : "Map Plan to Gyms"}
        onClose={closeModal}
      />

      <div className="cpmap-content">
        {step === 1 ? (
          <>
            {/* Search and Select All Section */}
            <div className="search-section">
              <div className="search-box">
                <input
                  type="search"
                  placeholder="Search gyms by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="search-input"
                />
                <button
                  onClick={handleSearch}
                  className="search-button"
                  title="Search">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    viewBox="0 0 16 16">
                    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                  </svg>
                </button>
              </div>
              <div className="select-all-container">
                <label className="checkbox-container">
                  <input
                    type="checkbox"
                    checked={
                      selectedGyms.length === gymData.length &&
                      gymData.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                  {/* <span className="checkmark"></span> */}
                  Select All Gyms ({selectedGyms.length}/{gymData.length})
                </label>
              </div>
            </div>

            {/* Gyms List Section */}
            <div className="gyms-list">
              {isLoading ? (
                <div className="loader-container">
                  <Loader />
                </div>
              ) : gymData.length === 0 ? (
                <div className="no-results">No gyms found</div>
              ) : (
                <div className="gym-items-container">
                  {gymData.map((gym) => (
                    <div
                      key={gym.value}
                      className="gym-item"
                      onClick={() => handleGymSelect(gym.value)}>
                      <div className="checkbox-container">
                        <input
                          type="checkbox"
                          checked={selectedGyms.includes(gym.value)}
                          onChange={(e) => e.stopPropagation()} // Prevent double trigger
                        />
                        {/* <span className="checkmark"></span> */}
                        <div className="gym-info">
                          <span className="gym-name">{gym.label}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : step === 2 ? (
          <>
            {/* Step 2: Select Plans */}
            <div className="plans-section">
              {selectedGyms.map((gymId) => {
                const gym = gymData.find((g) => g.value === gymId);
                const gymSpecificPlans = allPlansOptions.filter(
                  (plan) => plan?.info?.gym_id === gymId
                );

                return (
                  <div key={gymId} className="plan-selection-item">
                    <h3 className="gym-name">{gym?.label}</h3>
                    <div className="plan-dropdown">
                      <SelectDropdown
                        options={gymSpecificPlans}
                        selectedOption={selectedPlans[gymId]}
                        setSelectedOption={(selected) =>
                          setSelectedPlans((prev) => ({
                            ...prev,
                            [gymId]: selected,
                          }))
                        }
                        name="Plan"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            {/* Step 3: Preview Plan Details */}
            <CentralisedForm
              userInfoData={userInfoData}
              rowData={rowData}
              isDisabled={true}
              closeModal={closeModal}
            />
          </>
        )}

        <div className="action-buttons">
          {step === 2 || step === 3 ? (
            <button className="btn-secondary" onClick={handleBack}>
              Back
            </button>
          ) : null}

          {step === 1 || step === 2 ? (
            <button
              className="btn-primary"
              onClick={handleNext}
              disabled={step === 1 ? selectedGyms.length === 0 : false}>
              Next
            </button>
          ) : (
            <button className="btn-primary" onClick={handleSubmit}>
              Map Plans
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CPMapForm;
